import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { UserCircle, Save, Loader2, KeyRound, Download, Trash2, FileText, AlertTriangle } from 'lucide-react';
import type { Visionary } from '../types/visionary';
import type { Order } from '../types/orders';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function Account() {
  const { user, updateUserDetails } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [accountStats, setAccountStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    pointsEarned: 0,
    pointsUsed: 0,
    avgOrderValue: 0
  });
  
  // Get initial details from the user context
  const initialDetails = user?.visionary_details ? JSON.parse(user.visionary_details as string) : null;
  
  // Add state for live visionary data that refreshes from database
  const [currentVisionaryData, setCurrentVisionaryData] = useState<Visionary | null>(initialDetails);
  
  const [formData, setFormData] = useState<Partial<Visionary>>({
    name: initialDetails?.name || '',
    email: initialDetails?.email || '',
    address: initialDetails?.address || '',
    telephone: initialDetails?.telephone || ''
  });

  const [activeTab, setActiveTab] = useState('personal');

  // GDPR state
  const [showGdprExportModal, setShowGdprExportModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isExportingData, setIsExportingData] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Function to refresh visionary data from database
  const refreshVisionaryData = async () => {
    if (!initialDetails?.membership_number) return;
    
    try {
      const { data, error } = await supabase
        .from('visionaries')
        .select('*')
        .eq('membership_number', initialDetails.membership_number)
        .single();
      
      if (error) {
        console.error('Error refreshing visionary data:', error);
        return;
      }
      
      if (data) {
        setCurrentVisionaryData(data);
      }
    } catch (error) {
      console.error('Failed to refresh visionary data:', error);
    }
  };

  useEffect(() => {
    if (initialDetails) {
      refreshVisionaryData(); // Fetch live data on component mount
      fetchAccountStats();
    }
  }, []); // Empty dependency array - only run once on mount

  const fetchAccountStats = async () => {
    try {
      // Fetch orders
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('visionary_id', initialDetails.membership_number)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      // Set recent orders
      setRecentOrders(orders.slice(0, 5));

      // Calculate stats
      const totalOrders = orders.length;
      const totalSpent = orders.reduce((sum, order) => sum + order.total_amount, 0);
      const pointsEarned = orders.reduce((sum, order) => sum + (order.points_earned || 0), 0);
      const pointsUsed = orders.reduce((sum, order) => sum + (order.points_used || 0), 0);
      const avgOrderValue = totalOrders > 0 ? totalSpent / totalOrders : 0;

      setAccountStats({
        totalOrders,
        totalSpent,
        pointsEarned,
        pointsUsed,
        avgOrderValue
      });
    } catch (err) {
      console.error('Error fetching account stats:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => {
      let value = e.target.value;
      // Add telephone number formatting to allow only digits
      if (e.target.name === 'telephone') {
        value = value.replace(/\D/g, '');
      }
      return {
        ...prev,
        [e.target.name]: value
      };
    });
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (!initialDetails?.membership_number) {
      setError('Unable to update: Membership number not found');
      setLoading(false);
      return;
    }

    // Add telephone number length validation
    const MIN_TELEPHONE_LENGTH = 7;
    if (formData.telephone && formData.telephone.length < MIN_TELEPHONE_LENGTH) {
      setError(`Telephone number must be at least ${MIN_TELEPHONE_LENGTH} digits.`);
      setLoading(false);
      return;
    }

    try {
      // Use the RPC function to update visionary details
      const { error } = await supabase
        .rpc('update_visionary_details', {
          p_membership_number: initialDetails.membership_number,
          p_name: formData.name,
          p_email: formData.email,
          p_address: formData.address,
          p_telephone: formData.telephone
        });

      if (error) throw error;

      // Update the user context with new details
      const updatedDetails = {
        ...initialDetails,
        name: formData.name,
        email: formData.email,
        address: formData.address,
        telephone: formData.telephone
      };
      
      // Update the auth context
      updateUserDetails(updatedDetails);

      setSuccess(true);
    } catch (err) {
      console.error('Error updating account:', err);
      setError('Failed to update account details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      // Use the RPC function to update visionary password
      const { data, error } = await supabase.rpc('update_visionary_password', {
        p_email: initialDetails.email,
        p_new_password: newPassword
      });

      if (error) {
        console.error('RPC error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('No visionary found with this email address');
      }

      console.log('Password update successful');

      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess(true);
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError(err instanceof Error ? err.message : 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // GDPR Export Function for user's own data
  const exportMyGdprData = async () => {
    if (!initialDetails?.membership_number) return;
    
    setIsExportingData(true);
    try {
      // Try to get comprehensive data from RPC function first
      let gdprData = null;
      let useRpcData = false;
      
      try {
        const { data, error } = await supabase.rpc('get_visionary_gdpr_data', {
          p_membership_number: initialDetails.membership_number
        });
        
        if (!error && data) {
          gdprData = data;
          useRpcData = true;
        }
      } catch (rpcError) {
        console.log('RPC function not available, using fallback method');
      }

      // Fallback: Get orders data directly
      let userOrders = recentOrders;
      if (userOrders.length === 0) {
        try {
          const { data: ordersData } = await supabase
            .from('orders')
            .select('*, status')
            .eq('visionary_id', initialDetails.membership_number)
            .order('created_at', { ascending: false });
          userOrders = ordersData || [];
        } catch (orderError) {
          console.log('Could not fetch orders data');
        }
      }

      // Create PDF
      const pdf = new jsPDF();
      
      // Title
      pdf.setFontSize(20);
      pdf.text('My GDPR Data Export', 20, 30);
      
      // Export info
      pdf.setFontSize(12);
      pdf.text(`Export Date: ${new Date().toLocaleString()}`, 20, 45);
      pdf.text(`Data Subject: ${formData.name}`, 20, 55);
      pdf.text(`Membership Number: ${initialDetails.membership_number}`, 20, 65);
      
      // Personal Information Section
      pdf.setFontSize(16);
      pdf.text('Personal Information', 20, 85);
      
      const personalData = [
        ['Field', 'Value'],
        ['Name', formData.name || ''],
        ['Email', formData.email || ''],
        ['Address', formData.address || ''],
        ['Telephone', formData.telephone || ''],
        ['Loyalty Points', (currentVisionaryData?.loyalty_points || 0).toString()],
        ['Membership Number', initialDetails.membership_number.toString()]
      ];

      autoTable(pdf, {
        head: [personalData[0]],
        body: personalData.slice(1),
        startY: 95,
        theme: 'grid',
        headStyles: { fillColor: [128, 90, 213] }
      });

      // Orders Section
      if (useRpcData && gdprData?.orders && gdprData.orders.length > 0) {
        // Use comprehensive RPC data
        const finalY = (pdf as any).lastAutoTable.finalY || 95;
        
        pdf.setFontSize(16);
        pdf.text('Order History (Comprehensive)', 20, finalY + 20);
        
        const orderHeaders = ['Order ID', 'Date', 'Total', 'Points Used', 'Points Earned', 'Status'];
        const orderRows = gdprData.orders.map((order: any) => [
          order.order_id,
          new Date(order.created_at).toLocaleDateString(),
          `£${order.total_amount.toFixed(2)}`,
          order.points_used.toString(),
          order.points_earned.toString(),
          order.status
        ]);

        autoTable(pdf, {
          head: [orderHeaders],
          body: orderRows,
          startY: finalY + 30,
          theme: 'grid',
          headStyles: { fillColor: [128, 90, 213] }
        });

        // Order Items Details
        let currentY = (pdf as any).lastAutoTable.finalY + 20;
        
        gdprData.orders.forEach((order: any) => {
          if (currentY > 250) {
            pdf.addPage();
            currentY = 30;
          }
          
          pdf.setFontSize(14);
          pdf.text(`Order ${order.order_id} - Items`, 20, currentY);
          
          if (order.items && order.items.length > 0) {
            const itemHeaders = ['Product', 'Platform', 'Quantity', 'Price'];
            const itemRows = order.items.map((item: any) => [
              item.product_name,
              item.platform || 'N/A',
              item.quantity.toString(),
              `£${item.price_at_time.toFixed(2)}`
            ]);

            autoTable(pdf, {
              head: [itemHeaders],
              body: itemRows,
              startY: currentY + 10,
              theme: 'grid',
              headStyles: { fillColor: [79, 70, 229] }
            });
            
            currentY = (pdf as any).lastAutoTable.finalY + 20;
          } else {
            pdf.setFontSize(10);
            pdf.text('No items found for this order', 20, currentY + 10);
            currentY += 20;
          }
        });
      } else if (userOrders.length > 0) {
        // Use basic orders data
        const finalY = (pdf as any).lastAutoTable.finalY || 95;
        
        pdf.setFontSize(16);
        pdf.text('Order History (Basic)', 20, finalY + 20);
        
        const orderHeaders = ['Order ID', 'Date', 'Total', 'Points Used', 'Points Earned', 'Status'];
        const orderRows = userOrders.map((order: any) => [
          order.id,
          new Date(order.created_at).toLocaleDateString(),
          `£${order.total_amount.toFixed(2)}`,
          order.points_used?.toString() || '0',
          order.points_earned?.toString() || '0',
          order.status || 'Unknown'
        ]);

        autoTable(pdf, {
          head: [orderHeaders],
          body: orderRows,
          startY: finalY + 30,
          theme: 'grid',
          headStyles: { fillColor: [128, 90, 213] }
        });
      } else {
        // No orders found
        const finalY = (pdf as any).lastAutoTable.finalY || 95;
        pdf.setFontSize(14);
        pdf.text('No order history found', 20, finalY + 30);
      }

      // Footer
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text(`Page ${i} of ${pageCount}`, 20, pdf.internal.pageSize.height - 10);
        pdf.text('This document contains personal data processed under GDPR', 100, pdf.internal.pageSize.height - 10);
      }

      // Download the PDF
      pdf.save(`My_GDPR_Export_${formData.name?.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
      
      setShowGdprExportModal(false);
      
    } catch (err) {
      console.error('Error exporting GDPR data:', err);
      alert(`Failed to export data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsExportingData(false);
    }
  };

  // Delete Account Function (anonymize user's own account)
  const deleteMyAccount = async () => {
    if (!user?.username || !initialDetails?.membership_number) {
      alert('Unable to process account deletion request');
      return;
    }

    setIsDeletingAccount(true);
    try {
      console.log('Attempting to delete account:', {
        membership_number: initialDetails.membership_number,
        user_email: user.username
      });

      const { data, error } = await supabase.rpc('anonymize_visionary_account', {
        p_membership_number: initialDetails.membership_number,
        p_staff_email: user.username // Use user's own email as they're deleting their own account
      });

      console.log('RPC Response:', { data, error });

      if (error) {
        console.error('Supabase RPC Error:', error);
        throw new Error(error.message || error.details || JSON.stringify(error));
      }

      if (!data) {
        throw new Error('No response data from anonymization function');
      }

      if (!data.success) {
        throw new Error(data.message || 'Account deletion function returned failure');
      }

      setShowDeleteAccountModal(false);
      alert('Your account has been successfully deleted and anonymized in compliance with GDPR. You will be logged out shortly.');
      
      // Log the user out after successful deletion
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
      
    } catch (err) {
      console.error('Error deleting account:', err);
      
      let errorMessage = 'Unknown error occurred';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object') {
        errorMessage = JSON.stringify(err);
      }
      
      alert(`Failed to delete account: ${errorMessage}`);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  if (!initialDetails) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/70">Account not found</p>
      </div>
    );
  }

  return (
    <>
      {/* Premium Background Design */}
      <div className="fixed inset-0 z-0">
        {/* Base background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("/images/back2.png")' }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/40 via-purple-900/50 to-purple-950/60" />
        
        {/* Glass effect overlay */}
        <div className="absolute inset-0 backdrop-blur-[30px]" />
      </div>

      {/* Main Content */}
      <div className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto flex items-center z-10">
        <div className="w-full max-w-[1200px] mx-auto p-8">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden">
            <div className="grid grid-cols-[300px_1fr] divide-x divide-white/10">
              {/* Profile Info */}
              <div className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-32 h-32 bg-purple-500/20 rounded-full flex items-center justify-center mb-6">
                    <UserCircle className="w-16 h-16 text-purple-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-white">{formData.name}</h1>
                  <p className="text-white/70 mt-1">{formData.email}</p>
                  
                  <div className="w-full mt-8 pt-8 border-t border-white/10 space-y-4">
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-sm text-white/70 mb-1">Membership Number</p>
                      <p className="text-xl font-semibold text-purple-400">#{initialDetails.membership_number}</p>
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-white/70">Loyalty Points</p>
                        <button
                          onClick={refreshVisionaryData}
                          className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                          title="Refresh points balance"
                        >
                          Refresh
                        </button>
                      </div>
                      <p className="text-xl font-semibold text-white">{(currentVisionaryData?.loyalty_points || 0).toLocaleString()}</p>
                      <p className="text-sm text-white/50 mt-1">Value: £{((currentVisionaryData?.loyalty_points || 0) / 100).toFixed(2)}</p>
                    </div>

                    <div className="pt-4 space-y-2 text-center">
                      <div className="text-white/70">
                        <span className="text-white">{accountStats.totalOrders}</span> total orders · 
                        <span className="text-white ml-1">£{accountStats.totalSpent.toFixed(2)}</span> spent
                      </div>
                      <div className="text-white/70">
                        Saved <span className="text-white">£{(accountStats.pointsUsed / 100).toFixed(2)}</span> using{' '}
                        <span className="text-white">{accountStats.pointsUsed.toLocaleString()}</span> points
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-sm text-white/70 mb-2">Recent Activity</p>
                      <div className="space-y-2">
                        {recentOrders.slice(0, 3).map(order => (
                          <div key={order.id} className="text-sm">
                            <p className="text-white">Order #{order.id}</p>
                            <p className="text-white/50 text-xs">
                              {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Settings */}
              <div className="p-8">
                <div className="max-w-3xl">
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-lg font-semibold text-white">Account Settings</h2>
                  </div>

                  {/* Tabs for different settings */}
                  <div className="mb-8 border-b border-white/10">
                    <div className="flex gap-6">
                      <button 
                        onClick={() => setActiveTab('personal')}
                        className={`px-4 py-2 ${activeTab === 'personal' ? 'text-white border-b-2 border-purple-500' : 'text-white/70 hover:text-white'}`}
                      >
                        Personal Details
                      </button>
                      <button 
                        onClick={() => setActiveTab('security')}
                        className={`px-4 py-2 ${activeTab === 'security' ? 'text-white border-b-2 border-purple-500' : 'text-white/70 hover:text-white'}`}
                      >
                        Password & Security
                      </button>
                    </div>
                  </div>

                  {/* Personal Details Tab */}
                  {activeTab === 'personal' && (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {error && (
                        <div className="p-3 text-sm text-red-200 bg-red-500/20 border border-red-500/20 rounded-lg">
                          {error}
                        </div>
                      )}
                      
                      {success && (
                        <div className="p-3 text-sm text-emerald-200 bg-emerald-500/20 border border-emerald-500/20 rounded-lg">
                          Account details updated successfully
                        </div>
                      )}

                      <div className="space-y-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-white/70 mb-2">
                            Full Name
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all duration-200"
                          />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-white/70 mb-2">
                            Email Address
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all duration-200"
                          />
                        </div>

                        <div>
                          <label htmlFor="address" className="block text-sm font-medium text-white/70 mb-2">
                            Address
                          </label>
                          <textarea
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            required
                            rows={3}
                            className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all duration-200"
                          />
                        </div>

                        <div>
                          <label htmlFor="telephone" className="block text-sm font-medium text-white/70 mb-2">
                            Telephone
                          </label>
                          <input
                            type="tel"
                            id="telephone"
                            name="telephone"
                            value={formData.telephone}
                            onChange={handleChange}
                            required
                            minLength={7}
                            className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all duration-200"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end pt-4">
                        <button
                          type="submit"
                          disabled={loading}
                          className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Save Changes
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Password & Security Tab */}
                  {activeTab === 'security' && (
                    <div className="space-y-8">
                      {/* Password Change Section */}
                      <div className="bg-white/5 rounded-lg p-6">
                        <h3 className="text-lg font-medium text-white mb-4">Change Password</h3>
                        <form onSubmit={handlePasswordChange} className="space-y-6">
                          {passwordSuccess && (
                            <div className="p-3 text-sm text-emerald-200 bg-emerald-500/20 border border-emerald-500/20 rounded-lg">
                              Password updated successfully
                            </div>
                          )}
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-white/70 mb-2">
                                New Password
                              </label>
                              <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => {
                                  setNewPassword(e.target.value);
                                  setPasswordSuccess(false);
                                }}
                                minLength={8}
                                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white"
                                placeholder="Enter new password"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-white/70 mb-2">
                                Confirm Password
                              </label>
                              <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => {
                                  setConfirmPassword(e.target.value);
                                  setPasswordSuccess(false);
                                }}
                                minLength={8}
                                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white"
                                placeholder="Confirm new password"
                              />
                            </div>
                            {passwordError && (
                              <div className="p-3 text-sm text-red-200 bg-red-500/20 border border-red-500/20 rounded-lg">
                                {passwordError}
                              </div>
                            )}
                          </div>

                          <div className="flex justify-end pt-4">
                            <button
                              type="submit"
                              disabled={loading}
                              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                            >
                              {loading ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Updating Password...
                                </>
                              ) : (
                                <>
                                  <KeyRound className="w-4 h-4" />
                                  Update Password
                                </>
                              )}
                            </button>
                          </div>
                        </form>
                      </div>

                      {/* GDPR Data Rights Section */}
                      <div className="bg-white/5 rounded-lg p-6">
                        <h3 className="text-lg font-medium text-white mb-4">Data Rights (GDPR)</h3>
                        <p className="text-white/70 text-sm mb-6">
                          Manage your personal data in accordance with your privacy rights under GDPR.
                        </p>
                        
                        <div className="space-y-4">
                          {/* Export Data */}
                          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <FileText className="w-5 h-5 text-blue-400" />
                                  <h4 className="text-white font-medium">Export My Data</h4>
                                </div>
                                <p className="text-white/70 text-sm">
                                  Download a comprehensive report of all your personal data, order history, and account information.
                                </p>
                              </div>
                              <button
                                onClick={() => setShowGdprExportModal(true)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm whitespace-nowrap ml-4"
                              >
                                <Download className="w-4 h-4" />
                                Export Data
                              </button>
                            </div>
                          </div>

                          {/* Delete Account */}
                          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Trash2 className="w-5 h-5 text-red-400" />
                                  <h4 className="text-white font-medium">Delete My Account</h4>
                                </div>
                                <p className="text-white/70 text-sm">
                                  Permanently anonymize your personal data. This action cannot be undone.
                                </p>
                              </div>
                              <button
                                onClick={() => setShowDeleteAccountModal(true)}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm whitespace-nowrap ml-4"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Account
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* GDPR Export Confirmation Modal */}
      {showGdprExportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-500/20 p-2 rounded-lg">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Export My Data</h2>
                <p className="text-sm text-white/70">GDPR Article 15 Request</p>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4 mb-6">
              <p className="text-white text-sm">
                This will generate a comprehensive PDF containing all your personal data including:
              </p>
              <ul className="text-white/70 text-sm mt-2 space-y-1 list-disc list-inside">
                <li>Personal information (name, email, address, phone)</li>
                <li>Account details and loyalty points</li>
                <li>Complete order history and purchase details</li>
                <li>All transaction records</li>
              </ul>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowGdprExportModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={exportMyGdprData}
                disabled={isExportingData}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {isExportingData ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-500/20 p-2 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Delete My Account</h2>
                <p className="text-sm text-white/70">GDPR Article 17 Request</p>
              </div>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
              <p className="text-white text-sm mb-3">
                <strong>⚠️ WARNING:</strong> This action will permanently anonymize your personal data and cannot be undone.
              </p>
              <div className="text-red-300 text-xs space-y-1">
                <p>• Your name will become "Anonymised User"</p>
                <p>• Your email will become "anonymised@gdpr.deleted"</p>
                <p>• Your address and phone number will be cleared</p>
                <p>• You will lose access to your account permanently</p>
                <p>• Order history will be preserved for business records</p>
                <p>• Your membership number will be retained for analytics</p>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-4 mb-6">
              <p className="text-white/70 text-sm">
                Before proceeding, consider exporting your data first if you need a copy for your records.
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteAccountModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteMyAccount}
                disabled={isDeletingAccount}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {isDeletingAccount ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete My Account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 